/**
 * Registration Context - Phase 2 Implementation
 *
 * Centralized state management for the registration workflow.
 * Replaces prop drilling with context-based state management.
 *
 * Phase 2: Safe context implementation without breaking existing components
 */

import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useReducer,
} from 'react';
import { registrationService } from '../../../services/api/registration.service';
import type { AIMetadataResponse, RegistrationFormData } from '../../../shared/contracts/registration.contracts';
import { LayerMetadata, LayerType } from '../../../types/layerMetadata';
import '../layers'; // Import to trigger strategy registration
import { compositeLayerStrategy } from '../layers/CompositeLayerStrategy';
import { LayerStrategyFactory } from '../layers/LayerStrategy';

// ============================================================================
// STATE INTERFACES
// ============================================================================

export interface TaxonomySelection {
  layer: LayerType;
  category: string;
  subcategory: string;
  categoryName?: string;
  subcategoryName?: string;
}

export interface FileUploadResponse {
  fileUrl: string;
  fileName: string;
  fileType: string;
  thumbnailUrl?: string;
  uploadedAt: Date;
  file?: File; // Add file reference for submission
}

export interface Asset {
  id: string;
  name: string;
  layer: LayerType;
  metadata: LayerMetadata;
  [key: string]: any;
}

export interface RegistrationState {
  // Current step in the workflow (0-6)
  currentStep: number;

  // Layer and taxonomy selection
  selectedLayer: LayerType | null;
  taxonomySelection: TaxonomySelection | null;

  // File uploads
  uploadedFiles: FileUploadResponse[];
  isUploading: boolean;
  uploadError: string | null;

  // AI processing
  aiProcessing: boolean;
  aiResults: any | null;
  aiError: string | null;

  // Metadata
  layerMetadata: LayerMetadata | null;
  isMetadataValid: boolean;
  validationErrors: string[];

  // Asset relationships
  baseAsset: Asset | null;
  selectedComponents: Asset[];
  isVariant: boolean;

  // Form state
  formData: Record<string, any>;
  isDirty: boolean;
  hasUnsavedChanges: boolean;

  // Submission
  isSubmitting: boolean;
  submissionError: string | null;
  submissionSuccess: boolean;
  createdAsset: Asset | null;

  // UI state
  debugMode: boolean;
  showAdvanced: boolean;
}

// ============================================================================
// ACTION TYPES
// ============================================================================

export type RegistrationAction =
  // Navigation actions
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'ADVANCE_STEP' }
  | { type: 'GO_BACK_STEP' }
  | { type: 'RESET_WORKFLOW' }

  // Layer and taxonomy actions
  | { type: 'SET_SELECTED_LAYER'; payload: LayerType }
  | { type: 'SET_TAXONOMY_SELECTION'; payload: TaxonomySelection }
  | { type: 'RESET_UPLOAD_AND_AI_STATE' }

  // File upload actions
  | { type: 'START_FILE_UPLOAD' }
  | { type: 'ADD_UPLOADED_FILE'; payload: FileUploadResponse }
  | { type: 'REMOVE_UPLOADED_FILE'; payload: string }
  | { type: 'SET_UPLOAD_ERROR'; payload: string | null }
  | { type: 'CLEAR_UPLOADED_FILES' }

  // AI processing actions
  | { type: 'START_AI_PROCESSING' }
  | { type: 'SET_AI_RESULTS'; payload: any }
  | { type: 'SET_AI_ERROR'; payload: string | null }
  | { type: 'CLEAR_AI_RESULTS' }

  // Metadata actions
  | { type: 'SET_LAYER_METADATA'; payload: LayerMetadata | null }
  | { type: 'UPDATE_METADATA_FIELD'; payload: { field: string; value: any } }
  | {
      type: 'SET_METADATA_VALIDATION';
      payload: { isValid: boolean; errors: string[] };
    }

  // Asset relationship actions
  | { type: 'SET_BASE_ASSET'; payload: Asset | null }
  | { type: 'SET_IS_VARIANT'; payload: boolean }
  | { type: 'ADD_SELECTED_COMPONENT'; payload: Asset }
  | { type: 'REMOVE_SELECTED_COMPONENT'; payload: string }
  | { type: 'CLEAR_SELECTED_COMPONENTS' }

  // Form state actions
  | { type: 'UPDATE_FORM_DATA'; payload: Record<string, any> }
  | { type: 'SET_FORM_DIRTY'; payload: boolean }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean }

  // Submission actions
  | { type: 'START_SUBMISSION' }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_SUBMISSION_SUCCESS'; payload: Asset }
  | { type: 'SET_SUBMISSION_ERROR'; payload: string | null }
  | { type: 'SET_CREATED_ASSET'; payload: Asset }

  // UI state actions
  | { type: 'SET_DEBUG_MODE'; payload: boolean }
  | { type: 'SET_SHOW_ADVANCED'; payload: boolean };

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: RegistrationState = {
  currentStep: 0,
  selectedLayer: null,
  taxonomySelection: null,
  uploadedFiles: [],
  isUploading: false,
  uploadError: null,
  aiProcessing: false,
  aiResults: null,
  aiError: null,
  layerMetadata: null,
  isMetadataValid: false,
  validationErrors: [],
  baseAsset: null,
  selectedComponents: [],
  isVariant: false,
  formData: {},
  isDirty: false,
  hasUnsavedChanges: false,
  isSubmitting: false,
  submissionError: null,
  submissionSuccess: false,
  createdAsset: null,
  debugMode: false,
  showAdvanced: false,
};

// ============================================================================
// PLACEHOLDER HELPER FOR DEFAULT METADATA
// ============================================================================

/** Known numeric required fields (use 120 for tempo, 0 otherwise) */
const NUMERIC_PLACEHOLDERS: Record<string, number> = {
  tempo: 120,
};

/**
 * Fills required metadata fields with non-empty placeholders so that
 * strategy.validateMetadata(metadata) passes. Used when advancing to Review
 * without AI-generated metadata.
 */
function fillRequiredMetadataPlaceholders(
  metadata: Record<string, any>,
  requiredFields: string[]
): Record<string, any> {
  const result = { ...metadata };
  for (const field of requiredFields) {
    const value = result[field];
    const isEmpty =
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0);
    if (isEmpty) {
      if (NUMERIC_PLACEHOLDERS[field] !== undefined) {
        result[field] = NUMERIC_PLACEHOLDERS[field];
      } else if (field.toLowerCase().includes('tempo') || field === 'tempo') {
        result[field] = 120;
      } else if (
        field === 'genre' ||
        field === 'mood' ||
        field === 'danceStyle' ||
        field === 'atmosphere' ||
        field === 'style' ||
        field === 'occasion' ||
        field === 'colorScheme' ||
        field === 'primaryColors'
      ) {
        result[field] = Array.isArray(result[field]) ? ['general'] : ['general'];
      } else {
        result[field] = typeof value === 'number' ? 0 : 'Unknown';
      }
    }
  }
  return result;
}

/** Layer code to formData key for layer-specific metadata */
const LAYER_METADATA_KEY: Record<string, string> = {
  G: 'songMetadata',
  S: 'starMetadata',
  L: 'looksMetadata',
  M: 'movesMetadata',
  W: 'worldsMetadata',
};

const FORM_KEYS_TO_CLEAR_ON_TAXONOMY_CHANGE = [
  'files',
  'source',
  'description',
  'tags',
  'layerMetadata',
  'aiMetadata',
  'songMetadata',
  'starMetadata',
  'looksMetadata',
  'movesMetadata',
  'worldsMetadata',
  'compositeMetadata',
] as const;

function resetUploadAndAiArtifacts(formData: Record<string, any>): RegistrationState['formData'] {
  const nextFormData = { ...formData };
  for (const key of FORM_KEYS_TO_CLEAR_ON_TAXONOMY_CHANGE) {
    if (key === 'tags') {
      nextFormData[key] = [];
      continue;
    }
    if (key === 'files') {
      nextFormData[key] = [];
      continue;
    }
    if (key === 'layerMetadata' || key === 'aiMetadata' || key.endsWith('Metadata')) {
      nextFormData[key] = null;
      continue;
    }
    nextFormData[key] = '';
  }
  return nextFormData;
}

// ============================================================================
// REDUCER
// ============================================================================

function registrationReducer(
  state: RegistrationState,
  action: RegistrationAction
): RegistrationState {
  switch (action.type) {
    // Navigation actions
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };

    case 'ADVANCE_STEP': {
      const nextStep = Math.min(state.currentStep + 1, 6);
      // Composite (C): when advancing from step 3 to step 4 (Review), seed minimal composite metadata if missing (description, tags, songId, starId, lookId, moveId, worldId)
      if (state.selectedLayer === 'C' && state.currentStep === 3 && nextStep === 4) {
        const fd = state.formData || {};
        const descriptionOnLeave =
          state.aiResults?.data?.description?.trim() ||
          fd.creatorDescription?.trim() ||
          fd.description ||
          '';
        const hasMeta = (fd.layerMetadata && typeof fd.layerMetadata === 'object' && Object.keys(fd.layerMetadata).length > 0) ||
          (fd.compositeMetadata && typeof fd.compositeMetadata === 'object' && Object.keys(fd.compositeMetadata).length > 0);
        if (!hasMeta) {
          const components = fd.components ?? fd.layerSpecificData?.components;
          const layerToKey: Record<string, string> = { G: 'songId', S: 'starId', L: 'lookId', M: 'moveId', W: 'worldId' };
          let defaultCompositeMetadata: Record<string, any>;
          if (Array.isArray(components) && components.length === 5) {
            const meta: Record<string, any> = {
              layerType: 'composite',
              songId: '',
              starId: '',
              lookId: '',
              moveId: '',
              worldId: '',
              description: descriptionOnLeave || fd.description || fd.creatorDescription || fd.name || '',
              tags: Array.isArray(fd.tags) ? fd.tags : [],
            };
            for (const comp of components) {
              const layer = comp?.layer;
              const key = layer && layerToKey[layer];
              if (key) meta[key] = comp?.id ?? comp?._id ?? '';
            }
            defaultCompositeMetadata = meta;
          } else {
            const defaults = compositeLayerStrategy.getDefaultMetadata() as Record<string, any>;
            const compositeName = (fd.humanFriendlyName && String(fd.humanFriendlyName).trim()) ||
              (fd.name && String(fd.name).trim()) ||
              (fd.hfn && String(fd.hfn).trim()) ||
              'Untitled Composite';
            defaultCompositeMetadata = { ...defaults, compositeName };
          }
          return {
            ...state,
            currentStep: nextStep,
            formData: { ...fd, description: descriptionOnLeave, layerMetadata: defaultCompositeMetadata, compositeMetadata: defaultCompositeMetadata },
            layerMetadata: defaultCompositeMetadata as LayerMetadata,
          };
        }
      }
      // Non-Composite (G, S, L, M, W): when advancing from step 2 (File Upload) to step 3 (Review), seed default metadata if missing so validation passes
      if (state.currentStep === 2 && nextStep === 3 && state.selectedLayer && ['G', 'S', 'L', 'M', 'W'].includes(state.selectedLayer)) {
        const fd = state.formData || {};
        const layer = state.selectedLayer;
        const metadataForReview =
          (layer === 'S' && fd.starMetadata) ||
          (layer === 'G' && fd.songMetadata) ||
          (layer === 'L' && fd.looksMetadata) ||
          (layer === 'M' && fd.movesMetadata) ||
          (layer === 'W' && fd.worldsMetadata) ||
          fd.layerMetadata ||
          (fd as any).aiMetadata?.data?.layerMetadata ||
          state.layerMetadata;
        if (!metadataForReview) {
          try {
            const strategy = LayerStrategyFactory.getStrategy(layer);
            const defaults = strategy.getDefaultMetadata() as Record<string, any>;
            const requiredFields = strategy.getRequiredFields();
            const defaultMeta = fillRequiredMetadataPlaceholders(defaults, requiredFields);
            const metaKey = LAYER_METADATA_KEY[layer];
            const description =
              state.aiResults?.data?.description?.trim() ||
              state.formData?.creatorDescription?.trim() ||
              state.formData?.description ||
              '';
            const formUpdates: Record<string, any> = { ...fd, layerMetadata: defaultMeta, description };
            if (metaKey) formUpdates[metaKey] = defaultMeta;
            return { ...state, currentStep: nextStep, formData: formUpdates, layerMetadata: defaultMeta as LayerMetadata };
          } catch (_e) {
            // If strategy not found or error, advance without seeding
          }
        }
      }
      if (state.currentStep === 2 && nextStep === 3) {
        const description =
          state.aiResults?.data?.description?.trim() ||
          state.formData?.creatorDescription?.trim() ||
          state.formData?.description ||
          '';
        return { ...state, currentStep: nextStep, formData: { ...state.formData, description } };
      }
      if (state.selectedLayer === 'C' && state.currentStep === 3 && nextStep === 4) {
        const fd = state.formData || {};
        const description =
          state.aiResults?.data?.description?.trim() ||
          fd.creatorDescription?.trim() ||
          fd.description ||
          '';
        return { ...state, currentStep: nextStep, formData: { ...fd, description } };
      }
      return { ...state, currentStep: nextStep };
    }

    case 'GO_BACK_STEP':
      return { ...state, currentStep: Math.max(state.currentStep - 1, 0) };

    case 'RESET_WORKFLOW':
      return { ...initialState };

    // Layer and taxonomy actions
    case 'SET_SELECTED_LAYER':
      return {
        ...state,
        selectedLayer: action.payload,
        // Reset dependent state when layer changes
        taxonomySelection: null,
        layerMetadata: null,
        uploadedFiles: [],
        uploadError: null,
        isUploading: false,
        aiProcessing: false,
        aiResults: null,
        aiError: null,
        formData: {
          ...resetUploadAndAiArtifacts(state.formData),
          layer: action.payload,
          categoryCode: '',
          categoryName: '',
          subcategoryCode: '',
          subcategoryName: '',
          subcategoryNumericCode: '',
        },
      };

    case 'SET_TAXONOMY_SELECTION':
      console.log(`🔄 [REDUCER] SET_TAXONOMY_SELECTION - payload:`, action.payload);
      console.log(`🔄 [REDUCER] SET_TAXONOMY_SELECTION - old state:`, state.taxonomySelection);
      const newState = { ...state, taxonomySelection: action.payload };
      console.log(`🔄 [REDUCER] SET_TAXONOMY_SELECTION - new state:`, newState.taxonomySelection);
      return newState;

    // File upload actions
    case 'START_FILE_UPLOAD':
      return { ...state, isUploading: true, uploadError: null };

    case 'ADD_UPLOADED_FILE':
      return {
        ...state,
        uploadedFiles: [...state.uploadedFiles, action.payload],
        isUploading: false,
        uploadError: null,
      };

    case 'REMOVE_UPLOADED_FILE':
      return {
        ...state,
        uploadedFiles: state.uploadedFiles.filter(
          file => file.fileName !== action.payload
        ),
      };

    case 'SET_UPLOAD_ERROR':
      return { ...state, uploadError: action.payload, isUploading: false };

    case 'CLEAR_UPLOADED_FILES':
      return { ...state, uploadedFiles: [], uploadError: null };

    case 'RESET_UPLOAD_AND_AI_STATE':
      return {
        ...state,
        uploadedFiles: [],
        uploadError: null,
        isUploading: false,
        aiProcessing: false,
        aiResults: null,
        aiError: null,
        layerMetadata: null,
        formData: resetUploadAndAiArtifacts(state.formData),
      };

    // AI processing actions
    case 'START_AI_PROCESSING':
      return { ...state, aiProcessing: true, aiError: null };

    case 'SET_AI_RESULTS': {
      const description =
        action.payload?.data?.description?.trim() ||
        state.formData?.creatorDescription?.trim() ||
        state.formData?.description ||
        '';
      return {
        ...state,
        aiResults: action.payload,
        formData: { ...state.formData, description },
        aiProcessing: false,
        aiError: null,
      };
    }

    case 'SET_AI_ERROR':
      return { ...state, aiError: action.payload, aiProcessing: false };

    case 'CLEAR_AI_RESULTS':
      return { ...state, aiResults: null, aiError: null };

    // Metadata actions
    case 'SET_LAYER_METADATA':
      return {
        ...state,
        layerMetadata: action.payload,
        isDirty: true,
        hasUnsavedChanges: true,
      };

    case 'UPDATE_METADATA_FIELD':
      return {
        ...state,
        layerMetadata: {
          ...state.layerMetadata,
          [action.payload.field]: action.payload.value,
        } as LayerMetadata,
        isDirty: true,
        hasUnsavedChanges: true,
      };

    case 'SET_METADATA_VALIDATION':
      return {
        ...state,
        isMetadataValid: action.payload.isValid,
        validationErrors: action.payload.errors,
      };

    // Asset relationship actions
    case 'SET_BASE_ASSET':
      return { ...state, baseAsset: action.payload };

    case 'SET_IS_VARIANT':
      return { ...state, isVariant: action.payload };

    case 'ADD_SELECTED_COMPONENT':
      return {
        ...state,
        selectedComponents: [...state.selectedComponents, action.payload],
      };

    case 'REMOVE_SELECTED_COMPONENT':
      return {
        ...state,
        selectedComponents: state.selectedComponents.filter(
          comp => comp.id !== action.payload
        ),
      };

    case 'CLEAR_SELECTED_COMPONENTS':
      return { ...state, selectedComponents: [] };

    // Form state actions
    case 'UPDATE_FORM_DATA':
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
        isDirty: true,
      };

    case 'SET_FORM_DIRTY':
      return { ...state, isDirty: action.payload };

    case 'SET_UNSAVED_CHANGES':
      return { ...state, hasUnsavedChanges: action.payload };

    // Submission actions
    case 'START_SUBMISSION':
      return { ...state, isSubmitting: true, submissionError: null };

    case 'SET_SUBMISSION_SUCCESS':
      return {
        ...state,
        isSubmitting: false,
        submissionSuccess: true,
        createdAsset: action.payload,
        hasUnsavedChanges: false,
        isDirty: false,
      };

    case 'SET_SUBMISSION_ERROR':
      return { ...state, submissionError: action.payload, isSubmitting: false };

    // UI state actions
    case 'SET_DEBUG_MODE':
      return { ...state, debugMode: action.payload };

    case 'SET_SHOW_ADVANCED':
      return { ...state, showAdvanced: action.payload };

    // Additional submission actions
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };

    case 'SET_CREATED_ASSET':
      return { ...state, createdAsset: action.payload };

    default:
      return state;
  }
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

interface RegistrationContextType {
  state: RegistrationState;
  dispatch: React.Dispatch<RegistrationAction>;

  // Convenience action creators
  setCurrentStep: (step: number) => void;
  advanceStep: () => void;
  goBackStep: () => void;
  resetWorkflow: () => void;
  setSelectedLayer: (layer: LayerType) => void;
  setTaxonomySelection: (taxonomy: TaxonomySelection) => void;
  resetUploadAndAIState: () => void;
  updateFormData: (data: Record<string, any>) => void;
  updateMetadataField: (field: string, value: any) => void;
  validateCurrentStep: () => boolean;
  // File upload actions
  addUploadedFile: (file: FileUploadResponse) => void;
  removeUploadedFile: (fileName: string) => void;
  // Asset relationship actions
  setIsVariant: (isVariant: boolean) => void;
  setBaseAsset: (asset: Asset | null) => void;
  // Asset submission
  submitAsset: (files: File[]) => Promise<void>;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(
  undefined
);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface RegistrationProviderProps {
  children: ReactNode;
  initialState?: Partial<RegistrationState>;
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({
  children,
  initialState: providedInitialState,
}) => {
  const [state, dispatch] = useReducer(registrationReducer, {
    ...initialState,
    ...providedInitialState,
  });

  // Convenience action creators
  const setCurrentStep = useCallback((step: number) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
  }, []);

  const advanceStep = useCallback(() => {
    dispatch({ type: 'ADVANCE_STEP' });
  }, []);

  const goBackStep = useCallback(() => {
    dispatch({ type: 'GO_BACK_STEP' });
  }, []);

  const resetWorkflow = useCallback(() => {
    dispatch({ type: 'RESET_WORKFLOW' });
  }, []);

  const setSelectedLayer = useCallback((layer: LayerType) => {
    console.log(`🎯 [LAYER SELECTION] Setting selected layer: ${layer}`);
    console.log(`🎯 [LAYER SELECTION] Previous state:`, {
      currentStep: state.currentStep,
      selectedLayer: state.selectedLayer
    });
    dispatch({ type: 'SET_SELECTED_LAYER', payload: layer });
    console.log(`🎯 [LAYER SELECTION] Dispatch called - state will update on next render`);

    // Force a small delay to ensure state is updated before validation
    setTimeout(() => {
      console.log(`🎯 [LAYER SELECTION] Delayed check - new state should be:`, layer);
    }, 0);
  }, [state.currentStep, state.selectedLayer]);

  const setTaxonomySelection = useCallback((taxonomy: TaxonomySelection) => {
    console.log(`🎯 [CONTEXT] setTaxonomySelection called with:`, taxonomy);
    console.log(`🎯 [CONTEXT] Current taxonomy in state:`, state.taxonomySelection);
    dispatch({ type: 'SET_TAXONOMY_SELECTION', payload: taxonomy });
    console.log(`🎯 [CONTEXT] Dispatch called - state will update on next render`);
  }, [state.taxonomySelection]);

  const resetUploadAndAIState = useCallback(() => {
    dispatch({ type: 'RESET_UPLOAD_AND_AI_STATE' });
  }, []);

  const updateFormData = useCallback((data: Record<string, any>) => {
    dispatch({ type: 'UPDATE_FORM_DATA', payload: data });
  }, []);

  const updateMetadataField = useCallback((field: string, value: any) => {
    dispatch({ type: 'UPDATE_METADATA_FIELD', payload: { field, value } });
  }, []);

  // File upload actions
  const addUploadedFile = useCallback((file: FileUploadResponse) => {
    dispatch({ type: 'ADD_UPLOADED_FILE', payload: file });
  }, []);

  const removeUploadedFile = useCallback((fileName: string) => {
    dispatch({ type: 'REMOVE_UPLOADED_FILE', payload: fileName });
  }, []);

  // Asset relationship actions
  const setIsVariant = useCallback((isVariant: boolean) => {
    dispatch({ type: 'SET_IS_VARIANT', payload: isVariant });
  }, []);

  const setBaseAsset = useCallback((asset: Asset | null) => {
    dispatch({ type: 'SET_BASE_ASSET', payload: asset });
  }, []);

  const submitAsset = useCallback(async (files: File[]): Promise<void> => {
    if (files.length === 0) {
      throw new Error('No files selected for upload');
    }

    if (!state.selectedLayer || !state.taxonomySelection) {
      throw new Error('Missing layer or taxonomy selection');
    }

    try {
      dispatch({ type: 'SET_SUBMITTING', payload: true });

      // Build human-friendly name
      const humanFriendlyName = state.formData?.humanFriendlyName ||
        `${state.selectedLayer}.${state.taxonomySelection.category}.${state.taxonomySelection.subcategory}.001`;

      // Songs layer: use /assets/song endpoint with multipart payload
      if (state.selectedLayer === 'G') {
        const cleanFormData: RegistrationFormData = {
          ...state.formData,
          file: files[0],
          layer: state.selectedLayer,
          category: state.taxonomySelection.category,
          subcategory: state.taxonomySelection.subcategory,
          categoryName: state.taxonomySelection.categoryName,
          subcategoryName: state.taxonomySelection.subcategoryName,
          humanFriendlyName,
          creatorDescription: state.formData?.creatorDescription ?? state.formData?.description ?? state.formData?.name ?? '',
          assetType: 'base', // Always base assets
          songMetadata: state.formData?.songMetadata ?? (state.layerMetadata as Record<string, unknown>) ?? {},
          algorhythmMetadata: state.formData?.algorhythmMetadata,
        };

        // Use AI results if they match AIMetadataResponse shape
        const aiMetadata: AIMetadataResponse | undefined =
          state.aiResults &&
          typeof state.aiResults === 'object' &&
          'success' in state.aiResults &&
          state.aiResults.data != null
            ? (state.aiResults as AIMetadataResponse)
            : undefined;

        const result = await registrationService.registerSongAsset(cleanFormData, aiMetadata);

        const payload = result.data as any;
        const id = payload?.id != null ? String(payload.id).trim() : '';
        const createdAsset: Asset = {
          ...payload,
          id,
          name: payload?.humanFriendlyName ?? payload?.name ?? '',
          layer: state.selectedLayer!,
          metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
        };

        dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
        dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });
        return;
      }

      // Stars layer: use /assets/star endpoint with multipart payload
      if (state.selectedLayer === 'S') {
        const cleanFormData: RegistrationFormData = {
          ...state.formData,
          file: files[0],
          layer: state.selectedLayer,
          category: state.taxonomySelection.category,
          subcategory: state.taxonomySelection.subcategory,
          categoryName: state.taxonomySelection.categoryName,
          subcategoryName: state.taxonomySelection.subcategoryName,
          humanFriendlyName,
          creatorDescription: state.formData?.creatorDescription ?? state.formData?.description ?? state.formData?.name ?? '',
          assetType: 'base', // Always base assets
          starMetadata: state.formData?.starMetadata ?? (state.layerMetadata as Record<string, unknown>) ?? {},
        };

        const aiMetadata: AIMetadataResponse | undefined =
          state.aiResults &&
          typeof state.aiResults === 'object' &&
          'success' in state.aiResults &&
          state.aiResults.data != null
            ? (state.aiResults as AIMetadataResponse)
            : undefined;

        const result = await registrationService.registerStarAsset(cleanFormData, aiMetadata);

        const payload = result.data as any;
        const id = payload?.id != null ? String(payload.id).trim() : '';
        const createdAsset: Asset = {
          ...payload,
          id,
          name: payload?.humanFriendlyName ?? payload?.name ?? '',
          layer: state.selectedLayer!,
          metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
        };

        dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
        dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });
        return;
      }

      // Moves layer: use /assets/move endpoint with multipart payload
      if (state.selectedLayer === 'M') {
        const cleanFormData: RegistrationFormData = {
          ...state.formData,
          file: files[0],
          layer: state.selectedLayer,
          category: state.taxonomySelection.category,
          subcategory: state.taxonomySelection.subcategory,
          categoryName: state.taxonomySelection.categoryName,
          subcategoryName: state.taxonomySelection.subcategoryName,
          humanFriendlyName,
          creatorDescription: state.formData?.creatorDescription ?? state.formData?.description ?? state.formData?.name ?? '',
          assetType: 'base', // Always base assets
          movesMetadata: state.formData?.movesMetadata ?? (state.layerMetadata as Record<string, unknown>) ?? {},
        };

        const aiMetadata: AIMetadataResponse | undefined =
          state.aiResults &&
          typeof state.aiResults === 'object' &&
          'success' in state.aiResults &&
          state.aiResults.data != null
            ? (state.aiResults as AIMetadataResponse)
            : undefined;

        const result = await registrationService.registerMoveAsset(cleanFormData, aiMetadata);

        const payload = result.data as any;
        const id = payload?.id != null ? String(payload.id).trim() : '';
        const createdAsset: Asset = {
          ...payload,
          id,
          name: payload?.humanFriendlyName ?? payload?.name ?? '',
          layer: state.selectedLayer!,
          metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
        };

        dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
        dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });
        return;
      }

      // Looks layer: use /assets/look endpoint with multipart payload
      if (state.selectedLayer === 'L') {
        const cleanFormData: RegistrationFormData = {
          ...state.formData,
          file: files[0],
          layer: state.selectedLayer,
          category: state.taxonomySelection.category,
          subcategory: state.taxonomySelection.subcategory,
          categoryName: state.taxonomySelection.categoryName,
          subcategoryName: state.taxonomySelection.subcategoryName,
          humanFriendlyName,
          creatorDescription: state.formData?.creatorDescription ?? state.formData?.description ?? state.formData?.name ?? '',
          assetType: 'base', // Always base assets
          looksMetadata: state.formData?.looksMetadata ?? (state.layerMetadata as Record<string, unknown>) ?? {},
        };

        const aiMetadata: AIMetadataResponse | undefined =
          state.aiResults &&
          typeof state.aiResults === 'object' &&
          'success' in state.aiResults &&
          state.aiResults.data != null
            ? (state.aiResults as AIMetadataResponse)
            : undefined;

        const result = await registrationService.registerLookAsset(cleanFormData, aiMetadata);

        const payload = result.data as any;
        const id = payload?.id != null ? String(payload.id).trim() : '';
        const createdAsset: Asset = {
          ...payload,
          id,
          name: payload?.humanFriendlyName ?? payload?.name ?? '',
          layer: state.selectedLayer!,
          metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
        };

        dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
        dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });
        return;
      }

      // Worlds layer: use /assets/world endpoint with multipart payload
      if (state.selectedLayer === 'W') {
        const cleanFormData: RegistrationFormData = {
          ...state.formData,
          file: files[0],
          layer: state.selectedLayer,
          category: state.taxonomySelection.category,
          subcategory: state.taxonomySelection.subcategory,
          categoryName: state.taxonomySelection.categoryName,
          subcategoryName: state.taxonomySelection.subcategoryName,
          humanFriendlyName,
          creatorDescription: state.formData?.creatorDescription ?? state.formData?.description ?? state.formData?.name ?? '',
          assetType: 'base', // Always base assets
          worldsMetadata: state.formData?.worldsMetadata ?? (state.layerMetadata as Record<string, unknown>) ?? {},
        };

        const aiMetadata: AIMetadataResponse | undefined =
          state.aiResults &&
          typeof state.aiResults === 'object' &&
          'success' in state.aiResults &&
          state.aiResults.data != null
            ? (state.aiResults as AIMetadataResponse)
            : undefined;

        const result = await registrationService.registerWorldAsset(cleanFormData, aiMetadata);

        const payload = result.data as any;
        const id = payload?.id != null ? String(payload.id).trim() : '';
        const createdAsset: Asset = {
          ...payload,
          id,
          name: payload?.humanFriendlyName ?? payload?.name ?? '',
          layer: state.selectedLayer!,
          metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
        };

        dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
        dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });
        return;
      }

      // Composite (C): use POST /assets/composite with same structure as world; metadata = { songId, starId, lookId, moveId, worldId }
      if (state.selectedLayer === 'C') {
        const components = state.formData?.components ?? state.formData?.layerSpecificData?.components;
        const cleanFormData: RegistrationFormData = {
          file: files[0],
          layer: 'C',
          category: state.taxonomySelection.category,
          subcategory: state.taxonomySelection.subcategory,
          categoryName: state.taxonomySelection.categoryName,
          subcategoryName: state.taxonomySelection.subcategoryName,
          humanFriendlyName,
          creatorDescription: state.formData?.creatorDescription ?? state.formData?.description ?? state.formData?.name ?? '',
          assetType: 'base',
          ...state.formData,
        };
        (cleanFormData as any).components = Array.isArray(components) ? components : state.formData?.components;
        (cleanFormData as any).tags = state.formData?.tags ?? [];
        (cleanFormData as any).description = state.formData?.description ?? state.formData?.creatorDescription ?? state.formData?.name ?? '';

        const result = await registrationService.registerCompositeAsset(cleanFormData);

        const payload = result.data as any;
        const id = payload?.id != null ? String(payload.id).trim() : '';
        const createdAsset: Asset = {
          ...payload,
          id,
          name: payload?.humanFriendlyName ?? payload?.name ?? '',
          layer: 'C',
          metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
        };

        dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
        dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });
        return;
      }

      // Other layers (T, R, P, etc.): create asset using /assets/json
      const result = await registrationService.createAsset({
        ...state.formData,
        file: files[0],
        layer: state.selectedLayer,
        humanFriendlyName,
        assetType: 'base',
        category: state.taxonomySelection.category,
        subcategory: state.taxonomySelection.subcategory,
        creatorDescription: state.formData?.description || state.formData?.creatorDescription || state.formData?.name || '',
        baseAssetId: state.baseAsset?._id,
        metadata: state.formData,
      });

      const payload = result.data as any;
      const id = payload?.id != null ? String(payload.id).trim() : '';
      const createdAsset: Asset = {
        ...payload,
        id,
        name: payload?.humanFriendlyName ?? payload?.name ?? '',
        layer: state.selectedLayer!,
        metadata: payload?.layerMetadata ?? payload?.metadata ?? state.layerMetadata ?? {},
      };

      dispatch({ type: 'SET_CREATED_ASSET', payload: createdAsset });
      dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: createdAsset });

    } catch (error) {
      console.error('❌ [REGISTRATION CONTEXT] Asset submission failed:', error);
      dispatch({ type: 'SET_SUBMISSION_ERROR', payload: error instanceof Error ? error.message : 'Asset creation failed' });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', payload: false });
    }
  }, [state.selectedLayer, state.taxonomySelection, state.isVariant, state.baseAsset, state.formData, state.layerMetadata, state.aiResults]);

  const validateCurrentStep = useCallback((): boolean => {
    console.log(`🔍 [VALIDATION] Step ${state.currentStep} validation check:`, {
      selectedLayer: state.selectedLayer,
      taxonomySelection: state.taxonomySelection,
      uploadedFiles: state.uploadedFiles?.length || 0,
      aiResults: !!state.aiResults,
      layerMetadata: !!state.layerMetadata
    });

    try {
      // Composite (C): 5 steps with Component Selection at step 2, File Upload at step 3, Review at step 4
      if (state.selectedLayer === 'C') {
        switch (state.currentStep) {
          case 0:
            return !!state.selectedLayer;
          case 1: {
            const hasCategory = !!state.taxonomySelection?.category || !!state.formData?.categoryCode;
            const hasSubcategory = !!state.taxonomySelection?.subcategory || !!state.formData?.subcategoryCode;
            return hasCategory && hasSubcategory;
          }
          case 2: {
            const components = state.formData?.components ?? state.formData?.layerSpecificData?.components;
            if (!Array.isArray(components) || components.length !== 5) {
              console.log(`✅ [VALIDATION] Step 2 (C) - Component selection: false (need exactly 5, got ${components?.length ?? 0})`);
              return false;
            }
            const requiredLayers = ['G', 'S', 'L', 'M', 'W'];
            const layersPresent = new Set((components as { layer?: string }[]).map((c: { layer?: string }) => c?.layer));
            const hasAllLayers = requiredLayers.every((l) => layersPresent.has(l));
            console.log(`✅ [VALIDATION] Step 2 (C) - Component selection: ${hasAllLayers} (one per G/S/L/M/W)`);
            return hasAllLayers;
          }
          case 3: {
            const hasFiles = state.uploadedFiles.length > 0;
            console.log(`✅ [VALIDATION] Step 3 (C) - File upload: ${hasFiles}`);
            return hasFiles;
          }
          case 4: {
            // Composite Review: require 5 components with id or _id so we can build songId, starId, lookId, moveId, worldId for submit
            const components = state.formData?.components ?? state.formData?.layerSpecificData?.components;
            if (!Array.isArray(components) || components.length !== 5) {
              console.log(`❌ [VALIDATION] Step 4 (C) - Review: need exactly 5 components, got ${components?.length ?? 0}`);
              return false;
            }
            const requiredLayers = ['G', 'S', 'L', 'M', 'W'];
            const layersPresent = new Set((components as { layer?: string }[]).map((c: { layer?: string }) => c?.layer));
            if (!requiredLayers.every((l) => layersPresent.has(l))) {
              console.log(`❌ [VALIDATION] Step 4 (C) - Review: need one component per G/S/L/M/W`);
              return false;
            }
            const allHaveId = (components as { id?: string; _id?: string }[]).every(
              (c) => (c?.id && String(c.id).trim() !== '') || (c?._id && String(c._id).trim() !== '')
            );
            if (!allHaveId) {
              console.log(`❌ [VALIDATION] Step 4 (C) - Review: each component must have id or _id`);
              return false;
            }
            const hasDescription = (state.formData?.description?.trim() ?? '').length > 0;
            if (!hasDescription) {
              console.log(`❌ [VALIDATION] Step 4 (C) - Review: description required`);
              return false;
            }
            console.log(`✅ [VALIDATION] Step 4 (C) - Review: 5 components with ids and description`);
            return true;
          }
          default:
            return true;
        }
      }

      switch (state.currentStep) {
        case 0: // Layer selection
          const hasLayer = !!state.selectedLayer;
          console.log(`✅ [VALIDATION] Step 0 - Layer selection: ${hasLayer} (layer: ${state.selectedLayer})`);
          return hasLayer;
        case 1: // Taxonomy selection
          const hasCategoryInContext = !!state.taxonomySelection?.category;
          const hasSubcategoryInContext = !!state.taxonomySelection?.subcategory;
          const hasCategoryInForm = !!state.formData?.categoryCode;
          const hasSubcategoryInForm = !!state.formData?.subcategoryCode;

          const hasCategory = hasCategoryInContext || hasCategoryInForm;
          const hasSubcategory = hasSubcategoryInContext || hasSubcategoryInForm;
          const hasTaxonomy = hasCategory && hasSubcategory;

          console.log(`✅ [VALIDATION] Step 1 - Taxonomy selection: ${hasTaxonomy}`, {
            context: state.taxonomySelection,
            formData: { categoryCode: state.formData?.categoryCode, subcategoryCode: state.formData?.subcategoryCode },
            checks: { hasCategory, hasSubcategory, hasTaxonomy }
          });
          return hasTaxonomy;
        case 2: // File upload (Step 3 in UI)
          const hasFiles = state.uploadedFiles.length > 0;
          console.log(`✅ [VALIDATION] Step 2 - File upload: ${hasFiles} (${state.uploadedFiles.length} files)`);
          return hasFiles;
        case 3: // Step 4 in UI: Metadata review
          if (!state.selectedLayer) {
            console.log(`❌ [VALIDATION] Step 3 - Metadata review: no selected layer`);
            return false;
          }
          const fd = state.formData || {};
          let metadataForReview =
            (state.selectedLayer === 'S' && fd.starMetadata) ||
            (state.selectedLayer === 'G' && fd.songMetadata) ||
            (state.selectedLayer === 'L' && fd.looksMetadata) ||
            (state.selectedLayer === 'M' && fd.movesMetadata) ||
            (state.selectedLayer === 'W' && fd.worldsMetadata) ||
            ((state.selectedLayer as string) === 'C' && fd.compositeMetadata) ||
            fd.layerMetadata ||
            (fd as any).aiMetadata?.data?.layerMetadata ||
            state.layerMetadata;
          if (!metadataForReview) {
            console.log(`❌ [VALIDATION] Step 3 - Metadata review: no metadata`);
            return false;
          }
          const strategyForReview = LayerStrategyFactory.getStrategy(state.selectedLayer);
          const validationForReview = strategyForReview.validateMetadata(metadataForReview);
          console.log(`✅ [VALIDATION] Step 3 - Metadata review: ${validationForReview.isValid}`);
          return validationForReview.isValid;
        case 4: // Step 5 in UI: Final Review or Component Selection (T, R, P)
          if (['T', 'R', 'P'].includes(state.selectedLayer || '')) {
            const components = state.formData?.layerSpecificData?.components ?? state.formData?.components;
            const componentValid = Array.isArray(components) && components.length >= 2;
            console.log(`✅ [VALIDATION] Step 4 - Component selection: ${componentValid} (${components?.length || 0} components)`);
            return componentValid;
          }
          // G/S/L/M/W/B final review: require description (seeded from AI or creator when leaving file upload)
          const hasDescriptionStep4 = (state.formData?.description?.trim() ?? '').length > 0;
          if (!hasDescriptionStep4) {
            console.log(`❌ [VALIDATION] Step 4 - Final step: description required`);
            return false;
          }
          console.log(`✅ [VALIDATION] Step 4 - Final step: true`);
          return true;
        case 5:
          // T/R/P final review: require description
          const hasDescriptionStep5 = (state.formData?.description?.trim() ?? '').length > 0;
          if (!hasDescriptionStep5) {
            console.log(`❌ [VALIDATION] Step 5 - Final step: description required`);
            return false;
          }
          return true;
        default:
          console.log(`✅ [VALIDATION] Step ${state.currentStep} - Default: true`);
          return true;
      }
    } catch (error) {
      console.error('❌ [VALIDATION] Error validating step:', error);
      return false;
    }
  }, [
    state.currentStep,
    state.selectedLayer,
    state.taxonomySelection,
    state.uploadedFiles,
    state.aiResults,
    state.layerMetadata,
    state.formData,
  ]);

  const contextValue: RegistrationContextType = {
    state,
    dispatch,
    setCurrentStep,
    advanceStep,
    goBackStep,
    resetWorkflow,
    setSelectedLayer,
    setTaxonomySelection,
    resetUploadAndAIState,
    updateFormData,
    updateMetadataField,
    validateCurrentStep,
    // File upload actions
    addUploadedFile,
    removeUploadedFile,
    // Asset relationship actions
    setIsVariant,
    setBaseAsset,
    // Asset submission
    submitAsset,
  };

  return (
    <RegistrationContext.Provider value={contextValue}>
      {children}
    </RegistrationContext.Provider>
  );
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export const useRegistrationContext = (): RegistrationContextType => {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error(
      'useRegistrationContext must be used within a RegistrationProvider'
    );
  }
  return context;
};

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

export const useCurrentStep = () => {
  const { state } = useRegistrationContext();
  return state.currentStep;
};

export const useSelectedLayer = () => {
  const { state } = useRegistrationContext();
  return state.selectedLayer;
};

export const useLayerMetadata = () => {
  const { state } = useRegistrationContext();
  return state.layerMetadata;
};

export const useCanAdvanceStep = () => {
  const { validateCurrentStep } = useRegistrationContext();
  return validateCurrentStep();
};

export default RegistrationContext;
